import AccountBalanceOutlined from '@material-ui/icons/AccountBalanceOutlined'
import CloseOutlined from '@material-ui/icons/CloseOutlined'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { useState } from 'react'
import { Button } from '../../core'
import { LoadingDots } from '../../core/LoadingDots'
import { styled } from '../../styles'
import FinanceMarketplaceDialogContent from './financeMarketplace/FinanceMarketplaceDialogContent'
import LenderReferralDialogContent from './lenderReferrals/LenderReferralDialogContent'
import {
  CheckoutFinanceAction,
  CheckoutFinancingPricingOption,
  CheckoutFinancingPricingParams,
  ExpectedMilestonePaymentType,
  LenderType,
} from './types'

const StyledDialog = styled(Dialog)<{ isMobile: boolean }>(({ isMobile }) => ({
  '&.MuiDialog-paper': {
    width: isMobile ? '100%' : 'auto',
    margin: isMobile ? '10px' : 'auto',
  },
  '&.MuiPaper-root': {
    width: isMobile ? '100%' : 'auto',
    margin: isMobile ? '10px' : 'auto',
  },
  zIndex: 2000,
}))
const StyledDialogTitle = styled(DialogTitle)(() => ({
  paddingBottom: '0px',
}))
const DialogTitleContentContainer = styled('div')(() => ({
  width: '100%',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderBottomWidth: '1px',
}))
const DialogTitleText = styled('div')<{ isMobile: boolean }>(({ isMobile }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  fontSize: isMobile ? '17px' : '18px',
  fontWeight: isMobile ? 400 : 500,
}))
const StyledCloseButton = styled(CloseOutlined)(() => ({
  color: '#757575',
}))
const LoadingDotsWrapper = styled('div')(() => ({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  opacity: 0.5,
  backgroundColor: '#F6F6F6',
  zIndex: 10,
}))
const StyledDialogContent = styled(DialogContent)(() => ({
  padding: '8px 24px',
}))

const CheckoutFinancingDialog = (props: {
  open: boolean
  onClose: () => void
  onApply: (lender: LenderType) => Promise<void>
  onEditLoanAmount: (field) => void
  actionData: CheckoutFinanceAction
  currencySymbol: string
  expectedMilestonePayments?: ExpectedMilestonePaymentType[] | undefined
  isLoading: boolean
  lenders: LenderType[]
  marketPlaceLenders: LenderType[]
  referralLenders: LenderType[]
  translate: (text: string) => string
  pricingOptions: CheckoutFinancingPricingOption[]
  pricingOptionsLoading: boolean
  updatePricingParams: (newParams: CheckoutFinancingPricingParams) => void
  pricingParams: CheckoutFinancingPricingParams
}) => {
  const {
    open,
    onClose,
    onApply,
    onEditLoanAmount,
    actionData,
    expectedMilestonePayments,
    currencySymbol,
    isLoading,
    lenders,
    marketPlaceLenders,
    referralLenders,
    translate,
    updatePricingParams,
    pricingParams,
  } = props

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'))

  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleApplyNowButton = async (lender) => {
    setIsSubmitting(true)
    await onApply(lender)
    setIsSubmitting(false)
  }

  const handleOnClose = () => {
    if (!isSubmitting) {
      onClose()
    }
  }

  return (
    <StyledDialog open={open} onClose={handleOnClose} isMobile={isMobile} maxWidth="lg" fullScreen={isMobile}>
      <StyledDialogTitle>
        <DialogTitleContentContainer>
          <DialogTitleText isMobile={isMobile}>
            <AccountBalanceOutlined />
            <span>{translate('Explore available financing')}</span>
          </DialogTitleText>
          <div>
            <IconButton onClick={handleOnClose}>
              <StyledCloseButton />
            </IconButton>
          </div>
        </DialogTitleContentContainer>
      </StyledDialogTitle>
      <StyledDialogContent>
        {!!referralLenders?.length && (
          <LenderReferralDialogContent
            lenders={referralLenders}
            translate={translate}
            onApply={handleApplyNowButton}
            onEditLoanAmount={onEditLoanAmount}
            actionData={actionData}
            expectedMilestonePayments={expectedMilestonePayments}
            currencySymbol={currencySymbol}
            setIsSubmitting={setIsSubmitting}
          />
        )}

        {(isLoading || isSubmitting) && (
          <LoadingDotsWrapper>
            <LoadingDots />
          </LoadingDotsWrapper>
        )}

        {!!marketPlaceLenders?.length && (
          <FinanceMarketplaceDialogContent
            pricingOptions={props.pricingOptions}
            lenders={marketPlaceLenders}
            isLoading={props.pricingOptionsLoading}
            translate={translate}
            updatePricingParams={updatePricingParams}
            pricingParams={pricingParams}
          />
        )}

        {!lenders.length && !isLoading && (
          <Typography>{translate('There are no available lenders for your region at the moment.')}</Typography>
        )}
      </StyledDialogContent>
      <DialogActions>
        <Button onClick={onClose}>{translate('Close')}</Button>
      </DialogActions>
    </StyledDialog>
  )
}

export default CheckoutFinancingDialog
